import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';

const DataPreviewGoogleAnalyticsPage = lazy(
  () => import('~/features/onboarding/pages/data-preview-google-analytics-screen'),
);

/** Search params drive the screen end-to-end so a refresh on this URL still
 *  shows the right preview without depending on in-memory store state.
 *  - `connectionId`: drives the live `/metrics` query (validated as UUID so a
 *    typo'd id is caught at the route layer rather than surfacing as a 404).
 *  - `accountName` / `propertyId`: labels the preview renders alongside the
 *    metrics; written by the picker step. */
const searchSchema = z.object({
  connectionId: z.string().uuid(),
  accountName: z.string(),
  propertyId: z.string(),
  /** `'1'` when the picker just pointed this connection at a different GA4
   *  property (or set one for the first time) — the signal that Continue should
   *  kick off the 30-day backfill + daily report schedule. Absent means the
   *  operator opened the picker and changed nothing, so there is nothing to
   *  re-pull. A plain string rather than a boolean: search-param parsing here
   *  is string-based throughout this route tree. */
  resync: z.enum(['1']).optional(),
});

function DataPreviewGoogleAnalyticsRouteComponent() {
  const { connectionId, accountName, propertyId, resync } = Route.useSearch();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <DataPreviewGoogleAnalyticsPage
        connectionId={connectionId}
        accountName={accountName}
        propertyId={propertyId}
        shouldStartDailyPull={resync === '1'}
      />
    </Suspense>
  );
}

export const Route = createFileRoute(
  '/_authenticated/_tenant/onboarding/integration/google/google-analytics/data-preview',
)({
  validateSearch: searchSchema,
  component: DataPreviewGoogleAnalyticsRouteComponent,
});
